import { useSyncExternalStore } from 'react';

export type ToastKind = 'info' | 'success' | 'error';

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  /** ms; 0 = sticky */
  duration: number;
}

type Listener = () => void;

const MAX_VISIBLE = 4;
let toasts: Toast[] = [];
let nextId = 1;
const listeners = new Set<Listener>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function emit() {
  for (const l of listeners) l();
}

export function dismissToast(id: number) {
  const t = timers.get(id);
  if (t) clearTimeout(t);
  timers.delete(id);
  const before = toasts.length;
  toasts = toasts.filter((x) => x.id !== id);
  if (toasts.length !== before) emit();
}

export function showToast(message: string, kind: ToastKind = 'info', duration?: number): number {
  const text = message.trim();
  // Dedupe: identical visible message just gets its timer refreshed.
  const existing = toasts.find((t) => t.message === text && t.kind === kind);
  const d = duration ?? (kind === 'error' ? 6000 : 3500);
  if (existing) {
    schedule(existing.id, d);
    return existing.id;
  }
  const toast: Toast = { id: nextId++, kind, message: text, duration: d };
  toasts = [...toasts, toast];
  while (toasts.length > MAX_VISIBLE) {
    const oldest = toasts[0];
    dismissToast(oldest.id);
  }
  schedule(toast.id, d);
  emit();
  return toast.id;
}

function schedule(id: number, d: number) {
  const prev = timers.get(id);
  if (prev) clearTimeout(prev);
  if (d > 0) timers.set(id, setTimeout(() => dismissToast(id), d));
}

export const toast = {
  info: (m: string, d?: number) => showToast(m, 'info', d),
  success: (m: string, d?: number) => showToast(m, 'success', d),
  error: (m: string, d?: number) => showToast(m, 'error', d),
  dismiss: dismissToast,
  clear() {
    for (const t of toasts) dismissToast(t.id);
  },
};

function subscribe(l: Listener) {
  listeners.add(l);
  return () => listeners.delete(l);
}
function snapshot() {
  return toasts;
}

export function useToasts(): Toast[] {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
